import * as R from 'ramda'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import s from './styles.module.css'
import cx from 'classnames'

import Tile1 from './assets/tile1.png'
import Tile2 from './assets/tile2.png'
import Tile3 from './assets/tile3.png'
import Tile4 from './assets/tile4.png'
import { useMutation } from '@apollo/client'
import { checkEligibility as checkEligibilityMutation } from './mutations.graphql'
import cmsSurveyContext from 'contexts/cms-survey-context'
import { INITIAL_SERVICE_TYPE_ID } from 'constants/bridge'
import { parseDate, stringifyDate } from 'utilities/dates'
import usePusher from 'hooks/use-pusher'
import { useRouter } from 'next/router'
import { INSURANCE_COVERAGE_FLOW_QUESTION_ID } from 'components/sign-up/constants'
import useCurrentUser from 'hooks/use-current-user'
import { STEPS } from 'components/sign-up/insurance-coverage-flow/constants'
import { getCopay } from 'utilities/insurance/copay'
import { diagnosesByBmi, getBmi } from 'utilities/bmi'
import { useCountdown } from 'hooks/use-countdown'

const ELIGIBILITY_FALLBACK_SECONDS =
  Number(process.env.NEXT_PUBLIC_INSURANCE_ELIGIBILITY_COUNTDOWN_SECONDS) || 30

export const STATE_RESPONSES = {
  1137: 'AL',
  1138: 'AK',
  1139: 'AZ',
  1140: 'AR',
  1188: 'CA',
  1141: 'CO',
  1142: 'CT',
  1143: 'DE',
  1216: 'DC',
  1145: 'FL',
  1146: 'GA',
  1147: 'HI',
  1148: 'ID',
  1149: 'IN',
  1150: 'IA',
  1151: 'IL',
  1152: 'KS',
  1153: 'KY',
  1154: 'LA',
  1155: 'ME',
  1156: 'MD',
  1157: 'MA',
  1158: 'MI',
  1159: 'MN',
  1160: 'MS',
  1161: 'MT',
  1162: 'NE',
  1163: 'MO',
  1164: 'NV',
  1165: 'NH',
  1166: 'NJ',
  1167: 'NM',
  1168: 'NY',
  1169: 'NC',
  1170: 'ND',
  1171: 'OH',
  1172: 'OK',
  1173: 'OR',
  1174: 'PA',
  1175: 'RI',
  1176: 'SC',
  1177: 'SD',
  1178: 'TN',
  1179: 'TX',
  1180: 'UT',
  1181: 'VT',
  1182: 'VA',
  1183: 'WA',
  1184: 'WI',
  1185: 'WY',
  1186: 'WV',
}

const InsuranceCoveredLoading = ({ question, goToNextQuestion, responses }) => {
  const currentUserPayload = useCurrentUser()
  const me = currentUserPayload?.data?.me
  const {
    client,
    responseBySurveyQuestionId,
    setCopay,
    setEligibilityStatus,
    setEligibilityData,
    setPolicyMetadata,
    setPolicyRetryCount,
  } = useContext(cmsSurveyContext)
  const router = useRouter()
  const [checkStarted, setCheckStarted] = useState(false)

  const header = question?.text || 'Checking Insurance Eligibility'
  const [checkEligibility] = useMutation(checkEligibilityMutation)

  const {
    firstName,
    lastName,
    payerId,
    memberId,
    skipMembershipId,
    dateOfBirth,
    diagnoses,
    state,
    latestEligibilityStatus,
  } = useMemo(() => {
    const height = responseBySurveyQuestionId[10]?.responseValue
    const weight = responseBySurveyQuestionId[9]?.responseValue
    const bmi = getBmi({ height, weight })
    const bmiDiagnoses = diagnosesByBmi({ bmi })
    const previouslyAnsweredDiagnoses =
      responseBySurveyQuestionId[5425]?.responseValue?.split(',') || []
    const diagnoses = [...bmiDiagnoses, ...previouslyAnsweredDiagnoses].filter(
      Boolean
    )

    return {
      firstName: responseBySurveyQuestionId[100]?.responseValue,
      lastName: responseBySurveyQuestionId[101]?.responseValue,
      payerId: responseBySurveyQuestionId[1586]?.responseValue,
      memberId: responseBySurveyQuestionId[20250315]?.responseValue,
      skipMembershipId:
        responseBySurveyQuestionId[20250315]?.metadata?.skipMembershipId,
      dateOfBirth: parseDate(responseBySurveyQuestionId[11]?.responseValue),
      diagnoses,
      state:
        STATE_RESPONSES[
          parseInt(
            responseBySurveyQuestionId[202503192]?.surveyQuestionResponseId
          )
        ],
      latestEligibilityStatus: R.pathOr(
        null,
        ['user', 'client', 'latestClientEligibilityStatus'],
        me
      ),
    }
  }, [responseBySurveyQuestionId, me])

  // Check whether the latest eligibility status is pending
  const hasPendingEligibility =
    latestEligibilityStatus?.currentStep === 'POLICY_PENDING' ||
    latestEligibilityStatus?.currentStep === 'SERVICE_ELIGIBILITY_PENDING'

  useEffect(() => {
    const startCheck = async () => {
      setCheckStarted(true)
      await checkEligibility({
        variables: {
          input: {
            clientId: client.id,
            payerId,
            location: {
              state,
            },
            person: {
              firstName,
              lastName,
              dateOfBirth: stringifyDate(dateOfBirth),
            },
            serviceTypeId: INITIAL_SERVICE_TYPE_ID,
            memberId,
            skipMembershipId,
            clinicalInfo: {
              diagnoses,
            },
          },
        },
        onCompleted: (data: any) => {
          const status =
            data.checkEligibility.currentStep === STEPS.POLICY_INVALID &&
            data.checkEligibility.retryCount >= 1
              ? STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE
              : data.checkEligibility.currentStep

          setEligibilityStatus(status)
          setEligibilityData(data.serviceEligibilityMetadata)
          setPolicyMetadata(data.policyMetadata)
          setPolicyRetryCount(data.retryCount)
          switch (status) {
            case STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE:
              if (skipMembershipId) {
                const patientResponsibility =
                  data.checkEligibility.serviceEligibilityMetadata
                    ?.conditionalPatientResponsibilities?.[0]
                    ?.patientResponsibility ||
                  data.checkEligibility.serviceEligibilityMetadata
                    ?.patientResponsibility
                setCopay(
                  getCopay(
                    patientResponsibility,
                    data.checkEligibility?.eligibilityParams?.clinicalInfo
                      ?.diagnoses
                  )
                )
                router.push(
                  `/client-sign-up?insuranceCovered=true&questionId=${INSURANCE_COVERAGE_FLOW_QUESTION_ID}&recheck=true`
                )
              }
              break
            case STEPS.POLICY_PENDING:
            case STEPS.SERVICE_ELIGIBILITY_PENDING:
              break
            case STEPS.UNDER_18_INELIGIBLE:
            case STEPS.PROVIDER_ELIGIBILITY_INELIGIBLE:
            case STEPS.SERVICE_ELIGIBILITY_ELIGIBLE:
            case STEPS.SERVICE_ELIGIBILITY_INELIGIBLE:
            case STEPS.SERVICE_ELIGIBILITY_INEFFECTIVE:
            case STEPS.SERVICE_ELIGIBILITY_UNINSURED:
            case STEPS.POLICY_INVALID:
              router.push(
                `/client-sign-up?insuranceCovered=true&questionId=${INSURANCE_COVERAGE_FLOW_QUESTION_ID}&recheck=true`
              )
              break
            default:
              break
          }
        },
      })
    }

    if (!hasPendingEligibility && !checkStarted) {
      startCheck()
    }
  }, [
    hasPendingEligibility,
    checkStarted,
    checkEligibility,
    client.id,
    payerId,
    state,
    firstName,
    lastName,
    dateOfBirth,
    memberId,
    skipMembershipId,
    diagnoses,
    setEligibilityStatus,
    setEligibilityData,
    setPolicyMetadata,
    setPolicyRetryCount,
    router,
    setCopay,
  ])

  useCountdown(0, ELIGIBILITY_FALLBACK_SECONDS, () => {
    setEligibilityStatus(STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE)
    setCopay(getCopay(null, diagnoses))
    router.push(
      `/client-sign-up?insuranceCovered=true&questionId=${INSURANCE_COVERAGE_FLOW_QUESTION_ID}&recheck=true`
    )
  })

  usePusher('eligibility', `eligibility-updated-${client.id}`, (data: any) => {
    const status =
      data.currentStep === STEPS.POLICY_INVALID && data.retryCount >= 1
        ? STEPS.PROVIDER_ELIGIBILITY_ELIGIBLE
        : data.currentStep
    setEligibilityStatus(status)
    setEligibilityData(data.serviceEligibilityMetadata)
    setPolicyMetadata(data.policyMetadata)
    setPolicyRetryCount(data.retryCount)

    const patientResponsibility =
      data.serviceEligibilityMetadata?.conditionalPatientResponsibilities?.[0]
        ?.patientResponsibility ||
      data.serviceEligibilityMetadata?.patientResponsibility

    setCopay(
      getCopay(
        patientResponsibility,
        data.eligibilityParams?.clinicalInfo?.diagnoses
      )
    )

    if (
      data.currentStep === STEPS.POLICY_PENDING ||
      data.currentStep === STEPS.SERVICE_ELIGIBILITY_PENDING
    ) {
      return
    }
    router.push(
      `/client-sign-up?insuranceCovered=true&questionId=${INSURANCE_COVERAGE_FLOW_QUESTION_ID}&recheck=true`
    )
  })

  return (
    <div>
      <h2 className={s.header}>{header}</h2>
      <div className={s.animationContainer}>
        <div className={s.row}>
          <div className={cx(s.image, s.image1)}>
            <Image
              src={Tile1}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
          <div className={cx(s.image, s.image2)}>
            <Image
              src={Tile2}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
        </div>
        <div className={s.row}>
          <div className={cx(s.image, s.image4)}>
            <Image
              src={Tile3}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
          <div className={cx(s.image, s.image3)}>
            <Image
              src={Tile4}
              alt="Insurance Covered Loading"
              width={85}
              height={85}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default InsuranceCoveredLoading
